/**
 * POM-6010 — what counts as a staged Lot & NDC change.
 *
 * The review dialog's `changes` map drives four things at once: the row's CHANGED badge, the
 * Commit button's count, the print lock, and the commit payload. All four have to agree, so the
 * question "is this actually a change?" is answered once, here, rather than by each reader.
 *
 * Apply is the only way into that map and it hands back a complete selection whether or not the
 * user moved anything. Picking the lot the row already carries therefore staged a no-op: the row
 * badged Changed, the labels were held back until a commit, and core was sent a write it reports
 * as already applied. A selection equal to the row's recorded state is not a change.
 *
 * Same rule as `bulkOverrideStaging.isStagedChange` on the Bulk Update screen, kept separate
 * because the values compared are not the same shape — an NDC plus a lot allocation here, an NDC
 * or a quantity there.
 */

import { ndcDigits } from './bulkOverrideFilters';

/**
 * POM-6121 — the identity of a hand-recorded lot, for a selection that has no inventoryLotId yet.
 *
 * Compared case-INSENSITIVELY on the lot number, and on the NDC's digits, so 'abc123' typed for
 * one row and 'ABC123' typed for the next are recognised as the one physical lot rather than
 * committed as two claims against it.
 *
 * The BUD is part of the identity: one lot number legitimately repeats across shipments under
 * different expiry dates, and collapsing those would claim the wrong vial.
 */
export const manualLotKey = ({ lotNumber, ndc, expirationDate } = {}) =>
  `manual:${ndcDigits(ndc)}:${String(lotNumber ?? '')
    .trim()
    .toLowerCase()}:${expirationDate ?? ''}`;

/**
 * POM-6121 — the key a staged lot is compared and rendered by.
 *
 * Everything about a staged row used to be keyed on `inventoryLotId`, which a typed lot does not
 * have: every hand-recorded selection would have collapsed onto the one `null` key. This is the
 * single place that decides on the fallback, so no call site has to remember it.
 *
 * The same concept as `fillProductLotSelection.lotKey` on the single-item Fill Product panel, and
 * deliberately a local twin rather than an import across modules — the precedent this file's own
 * header records for `bulkOverrideStaging`. The shapes being compared differ: a bulk selection is
 * ONE lot covering the whole fill quantity, while the single-item panel holds several lots each
 * with its own quantity, so a shared helper would have to be true of both and is true of neither.
 */
export const lotKeyOf = (lot) => lot?.inventoryLotId ?? (lot ? manualLotKey(lot) : null);

/**
 * Was this lot recorded by hand rather than allocated from stock?
 *
 * Flagged explicitly rather than inferred from a missing `inventoryLotId`: the panel can produce a
 * selection that HAS an id — the operator claimed one of the product's real zero-quantity lots —
 * and that claim still posts to the unverified bucket, so it is not an ordinary allocation.
 */
export const isManualLotSelection = (lot) => !!lot && lot.isManualEntry === true;

/** Is this row's staged edit a hand-recorded claim? Drives the row's UNVERIFIED marker. */
export const hasManualSelection = (change) =>
  (change?.lotAllocations ?? []).some(isManualLotSelection);

/**
 * Is this selection exactly what the row already records?
 *
 * Both halves must match: same NDC on a different lot is a real change, and so is the same lot
 * under a different NDC. The lot is compared on its key rather than on the printed lot number,
 * because a lot number is not unique across NDCs.
 *
 * A multi-lot fill is never a no-op. v1 replaces the whole allocation with a single lot, so any
 * pick collapses several lots into one however familiar that lot looks.
 *
 * POM-6121: a hand-recorded selection is never a no-op either, even when it names the very lot the
 * row already carries. Recording by hand posts unverified quantity against that lot — a real
 * write, and the only way forward for a row whose recorded lot inventory has no quantity for — so
 * treating it as "nothing to change" would drop the one thing the operator came here to do.
 */
export const isSameAsRecorded = ({ item, ndc, lotAllocations }) => {
  if (!item) return false;

  const picked = lotAllocations ?? [];
  if (picked.some(isManualLotSelection)) return false;
  // POM-6121 — nor is a shortfall claim, for the same reason and by the same rule as above: it
  // posts unverified quantity against the lot, which is a real write. Naming the lot the row
  // already carries IS the action when that lot has since drained below the fill — the very case
  // the claim exists for — and treating it as "nothing to change" killed Apply on exactly it.
  if (picked.some((lot) => lot?.allowShortfallAsUnverified === true)) return false;

  if (ndcDigits(ndc) !== ndcDigits(item.effectiveNdc)) return false;

  const recorded = item.recordedLots ?? [];

  if (recorded.length !== 1 || picked.length !== 1) return false;

  return lotKeyOf(picked[0]) === lotKeyOf(recorded[0]);
};

/**
 * POM-6536 — does this Apply only add the shortfall permission to the lot the row already records?
 *
 * Then it is a claim, not a change: nothing on the label moves, so it commits through core's
 * claim-only path and no label is rebuilt. Same NDC, one recorded stock lot, and the one pick is
 * that lot carrying the permission.
 */
export const isClaimOnRecordedLot = ({ item, ndc, lotAllocations }) => {
  const [picked, ...rest] = lotAllocations ?? [];
  if (!item || !picked || rest.length > 0) return false;
  if (picked.allowShortfallAsUnverified !== true || isManualLotSelection(picked)) return false;
  // A hand-recorded entry cannot carry the permission, so core would drop the claim. Picking the
  // lot from stock replaces that entry, which is a change.
  if (isManualLotSelection(item.recordedLots?.[0])) return false;

  return isSameAsRecorded({
    item,
    ndc,
    lotAllocations: [{ ...picked, allowShortfallAsUnverified: false }],
  });
};

/**
 * POM-6121 — one picked stock lot, as Apply stages it.
 *
 * Always the WHOLE fill quantity, never a pre-split pair: what the lot holds can change between
 * the pick and the fill, so core is told how much is needed and works out how much is actually
 * short, in one transaction under one availability guard. `allowShortfallAsUnverified` is
 * permission, not instruction — core splits only when the lot really cannot cover it.
 */
export const buildStockLotSelection = ({ lot, fillQuantity }) => {
  const shortfall = Math.max(0, Number(fillQuantity || 0) - Number(lot.remainingQty || 0));

  return {
    inventoryLotId: lot.inventoryLotId,
    lotNumber: lot.lotNumber,
    quantity: fillQuantity,
    // `unverifiedQty` is for THIS screen only — buildCommitChanges maps named fields, so it never
    // reaches the wire. It is what lets the review bar total the batch without re-reading every
    // lot's balance; core recomputes the real split at fill time from the permission alone.
    ...(shortfall > 0 ? { allowShortfallAsUnverified: true, unverifiedQty: shortfall } : {}),
  };
};

/**
 * How much of this batch will be claimed as unverified.
 *
 * A hand-recorded lot claims its whole quantity — there is no verified stock behind it at all.
 * A short stock lot claims only the difference, which Apply recorded when it knew the balance.
 */
export const stagedUnverifiedQty = (changes) =>
  Object.values(changes ?? {}).reduce(
    (total, change) =>
      total +
      (change.lotAllocations ?? []).reduce(
        (rowTotal, lot) =>
          rowTotal +
          (isManualLotSelection(lot) ? Number(lot.quantity || 0) : Number(lot.unverifiedQty || 0)),
        0
      ),
    0
  );

/**
 * POM-6121 — the `changes` map as commitBulkLabelLotNdc wants it.
 *
 * A lot selection carries its whole identity on the wire, not just an id:
 *
 *  - `inventoryLotId` is NULL for a lot number typed by hand, so core resolves-or-creates the row
 *    from the lot number, NDC and BUD instead.
 *  - `isManualEntry` is sent explicitly rather than inferred from that missing id, because the
 *    panel can also claim a real zero-quantity row — that claim HAS an id and still posts to the
 *    unverified bucket, which nothing else in the payload would say.
 *
 * Two rows of one batch that named the same hand-recorded lot send the same three values, so core
 * creates the lot once and posts both claims against it.
 *
 * Here rather than in the dialog because it is the mirror of `stageLotNdcChange` below: one module
 * owns what a staged change looks like going in and going out.
 *
 * POM-6535 — sorted into `gridOrderLineItemIds` order, not staging order: when the batch asks a
 * lot for more than it holds, core gives it to the rows in the order they arrive (AC 5), so the
 * rows that keep it are the ones the operator sees first. Rows missing from the list go last.
 */
export const buildCommitChanges = (changes, gridOrderLineItemIds = []) => {
  const gridPosition = new Map(gridOrderLineItemIds.map((id, index) => [id, index]));
  const positionOf = (requestLineItemId) =>
    gridPosition.get(requestLineItemId) ?? gridOrderLineItemIds.length;

  return Object.entries(changes ?? {})
    .sort(([leftId], [rightId]) => positionOf(leftId) - positionOf(rightId))
    .map(([requestLineItemId, change]) => ({
      requestLineItemId,
      ndc: change.ndc,
      // No per-allocation ndc: BulkLabelLotSelectionInput does not declare one, and GraphQL
      // refuses the whole operation on an unknown input field rather than dropping it. Core reads
      // the row-level `ndc` above for both the resolution key and resolveOrCreateLotForFill.
      lotAllocations: (change.lotAllocations ?? []).map((lot) => ({
        inventoryLotId: lot.inventoryLotId ?? null,
        lotNumber: lot.lotNumber,
        expirationDate: lot.expirationDate ?? null,
        quantity: lot.quantity,
        isManualEntry: !!lot.isManualEntry,
        // POM-6121 — the operator's permission to claim what the lot cannot cover. Sent only when
        // granted: a bare `false` on every other row would read as a decision nobody made.
        ...(lot.allowShortfallAsUnverified ? { allowShortfallAsUnverified: true } : {}),
      })),
      // POM-6537 — BULK_APPLY for a row moved by "Also apply"; core records SINGLE when absent.
      ...(change.changeSource ? { changeSource: change.changeSource } : {}),
    }));
};

/**
 * The next `changes` map for one Apply.
 *
 * A no-op DELETES rather than merely skips. Applying the recorded values back onto a row that was
 * already edited is how a user takes an edit back from inside the picker, and leaving the
 * superseded entry staged would keep the row badged Changed after they had put it back.
 */
export const stageLotNdcChange = ({
  changes,
  item,
  requestLineItemId,
  ndc,
  lotAllocations,
  changeSource,
}) => {
  const next = { ...(changes ?? {}) };

  // POM-6536 — a claim on the recorded lot is staged as a claim, so it leaves no change behind.
  if (
    isSameAsRecorded({ item, ndc, lotAllocations }) ||
    isClaimOnRecordedLot({ item, ndc, lotAllocations })
  ) {
    delete next[requestLineItemId];
  } else {
    next[requestLineItemId] = { ndc, lotAllocations, ...(changeSource ? { changeSource } : {}) };
  }

  return next;
};
